<template>
  <div>
    <week-strip ref="weekStrip" class="mb-3" :model-value="selectedDate" :today="today" :can-leave="confirmLeaveDay" @update:model-value="selectDate" />
    <day-registration-editor ref="editor" :key="editorKey" :date="selectedDate" :locked="selectedDateLocked" :phases-enabled="phasesEnabled" :reference-enabled="referenceEnabled" @saved="onDaySaved" />
    <div class="text-center mt-1">
      <button class="btn btn-link text-secondary mb-0" @click="openAbsenceForm">
        <i class="material-icons align-middle me-1" style="font-size:1rem">event_busy</i>{{ $t('timeRegistration.registerAbsence') }}
      </button>
    </div>

    <absence-form v-if="absenceOpen" ref="absenceForm" :key="absenceKey" class="mt-2" :date="selectedDate" :absence-types="absenceTypes" @saved="onAbsenceSaved" @cancel="absenceOpen = false" />
  </div>
</template>

<script>
import api from "../../services/api";
import DayRegistrationEditor from "./DayRegistrationEditor.vue";
import AbsenceForm from "./AbsenceForm.vue";
import WeekStrip from "./WeekStrip.vue";
import { dateStr } from "../../utils/salaryPeriod.js";

/**
 * DayRegistrationTab — the "Skrá dag" tab of TimeRegistration.vue on end-of-day registration
 * (EnableEndOfDayTimeRegistration): pick a day in WeekStrip (no future days), register its hours per project
 * with DayRegistrationEditor, or register an absence (AbsenceForm). Review and period
 * submission stay in Yfirlit.
 */
export default {
  name: "DayRegistrationTab",
  components: { DayRegistrationEditor, AbsenceForm, WeekStrip },
  props: {
    currentUserId: { type: String, default: null },
    phasesEnabled: { type: Boolean, default: false },
    referenceEnabled: { type: Boolean, default: false },
    absenceTypes: { type: Array, default: () => [] },
  },
  data() {
    const today = dateStr(new Date());
    return {
      today,
      selectedDate: today,
      lockedPeriods: [],
      checkedPeriods: [],
      editorKey: 0,
      absenceOpen: false,
      absenceKey: 0,
    };
  },
  computed: {
    selectedDateLocked() { return this.lockedPeriods.some(p => this.selectedDate >= p.from && this.selectedDate <= p.to); },
  },
  created() { this.fetchLock(this.selectedDate); },
  methods: {
    selectDate(date) {
      this.selectedDate = date;
      this.fetchLock(date);
    },
    /** Asks first when unsaved changes on the day would be lost. Also used by TimeRegistration.vue. */
    confirmLeaveDay() { return this.$refs.editor.confirmDiscardChanges(); },
    /** Remember submitted/approved periods so the editor shows them as locked — asked once per salary period. */
    async fetchLock(date) {
      if (this.checkedPeriods.some(p => date >= p.from && date <= p.to)) return;
      try {
        const r = await api.get(`/Timesheet/period/approvals?date=${date}`);
        // One row per user (all users for admins); Status null = the period is still open
        const mine = (r.data || []).find(a => a.userId === this.currentUserId);
        if (!mine) return;
        const period = { from: mine.periodStart.substring(0, 10), to: mine.periodEnd.substring(0, 10) };
        this.checkedPeriods.push(period);
        if (mine.status != null) this.lockedPeriods.push(period);
      } catch (e) { console.error(e); } // eslint-disable-line no-console
    },
    openAbsenceForm() {
      this.absenceKey++;
      this.absenceOpen = true;
    },
    // The absence cap follows the day's work, so refresh it when the day is saved
    onDaySaved() {
      this.$refs.absenceForm?.reload();
      this.$refs.weekStrip.reload();
    },
    onAbsenceSaved() {
      this.absenceOpen = false;
      this.$refs.weekStrip.reload();
      // The absence (and the lower dagvinna ceiling) must show: remount the editor, unless that loses unsaved changes
      if (this.$refs.editor.hasPendingChanges) this.$refs.editor.onAbsenceEdited();
      else this.editorKey++;
    },
  },
};
</script>
